// Tiny shared UI store for the workspace settings dialog. The dialog
// itself lives in <AnalysisFeature />, but the trigger gear icon
// belongs in <AnalysisHeader /> next to the State pill (Christian's
// brief 2026-05-10 §"workspace gear next to state pill — own row was
// a waste"). Keep this store narrow: open/close flag + the active
// settings tab (SETTINGS-01 — tabbed modal; re-opening returns to the
// last-viewed tab).
import { create } from "zustand";

// SETTINGS-01 — the settings categories, in menu order.
// SB6-ANA-FILT-05 adds "Filter Behavior" (count-unit setting, D3).
export type SettingsTab =
  | "layout"
  | "viewer"
  // ANA-R3-TILE-03 — Tiles display settings (max-zoom cap + tile size).
  | "tiles"
  | "boundingBoxes"
  | "list"
  | "imageStates"
  | "filterBehavior"
  // SB6-ANA-CACHE-03 — image-cache storage management (usage, pins, budget).
  | "storage"
  | "shortcuts"
  | "general"
  // DI-3-S61 — the data set's history (intake/v1 getIntakeHistory), LAST.
  | "history";

export const SETTINGS_TABS: readonly SettingsTab[] = [
  "layout",
  "viewer",
  "tiles",
  "boundingBoxes",
  "list",
  "imageStates",
  "filterBehavior",
  "storage",
  "shortcuts",
  "general",
  "history",
];

interface WorkspaceUiState {
  settingsOpen: boolean;
  settingsTab: SettingsTab;
  openSettings: () => void;
  /**
   * SB6-ANA-HEADER-02 — open the Settings modal directly on a given tab
   * (used by the burger's "Settings" item which always opens Layout &
   * Widgets context first).
   */
  openSettingsTab: (tab: SettingsTab) => void;
  closeSettings: () => void;
  setSettingsTab: (tab: SettingsTab) => void;

  /**
   * SB6-ANA-HEADER-02 — "Reset analysis widgets to default" moved from the
   * canvas icon into Settings → Layout & Widgets. `AnalysisWorkspace` owns
   * the imperative reset (it operates on local state + per-user
   * localStorage), so it registers its `handleResetLayout` here on mount
   * and the SettingsModal invokes it. `null` while no workspace is mounted
   * (e.g. a pop-out without the grid) — the Settings action hides itself.
   */
  resetLayout: (() => void) | null;
  registerResetLayout: (fn: (() => void) | null) => void;

  /**
   * SB6-ANA-HEADER-02 — the edit-lock self-release control, lifted from the
   * (now removed) standalone lock-release row so the burger's "Release"
   * (self-unassign) item can drop the lock first when held. `LockMount`
   * registers `lockControls.release` here; `null` when no writer lock
   * effect is mounted. Idempotent — a no-op when nothing is held.
   */
  releaseLock: (() => void) | null;
  registerReleaseLock: (fn: (() => void) | null) => void;
}

export const useWorkspaceUiStore = create<WorkspaceUiState>((set) => ({
  settingsOpen: false,
  settingsTab: "layout",
  openSettings: () => {
    set({ settingsOpen: true });
  },
  openSettingsTab: (tab) => {
    set({ settingsOpen: true, settingsTab: tab });
  },
  closeSettings: () => {
    set({ settingsOpen: false });
  },
  setSettingsTab: (tab) => {
    set({ settingsTab: tab });
  },

  resetLayout: null,
  registerResetLayout: (fn) => {
    set({ resetLayout: fn });
  },

  releaseLock: null,
  registerReleaseLock: (fn) => {
    set({ releaseLock: fn });
  },
}));
